import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {HTH} from "../HTH";
import {gsap} from "gsap";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {HTHGameAssets} from "../HTHGameAssets";
import {HTHSymbol} from "../symbols/HTHSymbol";
import {HTHGameConfig} from "../HTHGameConfig";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {HTHEnhancerSymbol} from "../symbols/HTHEnhancerSymbol";
import {HTHSafeSymbol} from "../symbols/HTHSafeSymbol";


export class HTHIndividualWinTextView extends PIXI.Container implements StageLayer {

    private readonly _maxWinNumberWidth: number = 100;
    private readonly _maxWaysNumberWidth: number = 75;
    private textStyle: PIXI.TextStyle = new PIXI.TextStyle({
        fontSize: 27,
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowAngle: 1.9,
        dropShadowDistance: 1,
        fill: [
            "#f7e67e",
            "#ff8800",
            "#ff0000"
        ],
        fontFamily: "Arial Black",
        fontStyle: "italic",
        stroke: 0x000000,//"#0d1a3f",
        lineJoin: "round",
        strokeThickness: 8
    });

    constructor() {
        super();
        this.name = "HTHIndividualWinTextView";
    }

    public playWinNumbers(betWayWinData: IBetWayWinData, skipped: boolean, symPosition: number[]): gsap.core.Timeline {
        let text = HTH.apiPlugin.currency.formatValue(MathHelper.roundToDecimals(betWayWinData.multipliedAmount, 2)),
            totalWinWays: number = betWayWinData.numberOfCombinations;
        let startScale = 1;
        let endScale = startScale;
        let lingerTime = 0;
        const textHolder: PIXI.Sprite = new PIXI.Sprite();
        const winNumber = new PIXI.BitmapText(text, {
                fontName: HTHGameAssets.BITMAP_FONT_WIN_COUNT_UP,
                fontSize: 15
            }),
            waysNumber = new PIXI.Text(totalWinWays.toString(), this.textStyle),
            waysText = new PIXI.Text(NolimitApplication.apiPlugin.translations.translate(`Way${totalWinWays > 1 ? "s" : ""}`), this.textStyle);
        winNumber.anchor = new PIXI.Point(0, 1);
        winNumber.position.set(0, 0);

        waysNumber.anchor = new PIXI.Point(0, 0);
        waysText.anchor = new PIXI.Point(0, 0);

        textHolder.alpha = 0;
        textHolder.visible = false;
        textHolder.addChild(winNumber, waysText, waysNumber);
        textHolder.anchor.set(1, 0);
        this.addChild(textHolder);
        winNumber.updateTransform();
        if (winNumber.width > this._maxWinNumberWidth) {
            const scale: number = MathHelper.roundToDecimals(this._maxWinNumberWidth / winNumber.width, 2);
            winNumber.scale.set(scale);
        }
        const totalwaysWidth: number = waysNumber.width + waysText.width;
        if (totalwaysWidth > this._maxWaysNumberWidth) {
            const scaleWays: number = MathHelper.roundToDecimals(this._maxWaysNumberWidth / totalwaysWidth, 2);
            waysNumber.scale.set(scaleWays);
            waysText.scale.set(scaleWays);
        }
        const startPos: number = (winNumber.width - waysNumber.width - waysText.width) * 0.5;
        waysNumber.position.set(startPos, 30);
        waysText.position.set(startPos + waysNumber.width, 30);
        const startPositions = this.getStartPosition(symPosition, winNumber.width);
        textHolder.position.set(startPositions[0], startPositions[1]);

        const timeline: gsap.core.Timeline = gsap.timeline();
        timeline.add([
            () => {
                textHolder.visible = true;
            },
            gsap.to(textHolder, {duration: 0, alpha: 1}),
            gsap.to(textHolder, {duration: 0.8, y: startPositions[1] - startPositions[2]})
        ], 0);
        timeline.add(gsap.to(textHolder, {duration: 0.4, alpha: 0, delay: lingerTime}));
        timeline.add(() => {
            if (winNumber.parent) {
                this.removeChild(winNumber);
            }
            winNumber.destroy();
            if (waysNumber.parent) {
                this.removeChild(waysNumber);
            }
            waysNumber.destroy();
            waysText.destroy();
            textHolder.destroy();
        });
        if (skipped) {
            timeline.timeScale(4);
        }

        return timeline;
    }

    public findLastNoWildSymbolPosition(betWayWinData: IBetWayWinData): number[] {
        for (let reelId = 5; reelId > -1; reelId--) {
            for (let symId = 0; symId < 4; symId++) {
                if (!betWayWinData.reelWinPositions[reelId][symId]) {
                    continue;
                }
                const sym: HTHSymbol = HTH.reelArea.getSymbol([reelId, symId]);
                if (!sym) {
                    continue;
                }

                if (!sym.visible) {
                    continue;
                }

                if (sym.symName === "W" || sym.symName === "RW" || sym.symName === "XN" || sym.symName === "BL") {
                    continue;
                }
                if (sym.symName === "EB" && !(<HTHEnhancerSymbol>sym).isEnhancerOpen) {
                    continue;
                }
                if (sym.symName === "EB" && ((<HTHEnhancerSymbol>sym).revealedSymbol?.symName === "W" || (<HTHEnhancerSymbol>sym).revealedSymbol?.symName === "RW") || (<HTHEnhancerSymbol>sym).revealedSymbol?.symName === "XN") {
                    continue;
                }
                if (sym.symName === "SA" && ((<HTHSafeSymbol>sym).revealedSymbol?.symName === "W" || (<HTHSafeSymbol>sym).revealedSymbol?.symName === "RW") || (<HTHSafeSymbol>sym).revealedSymbol?.symName === "XN") {
                    continue;
                }

                return [reelId, symId];
            }
        }
        return [0, 0];
    }

    private getStartPosition(symPosition: number[], textWidth: number): number[] {
        const sym: HTHSymbol = HTH.reelArea.getSymbol(symPosition),
            reelId = symPosition[0],
            symId = symPosition[1];
        const viewPosition: PIXI.Point = HTHGameConfig.reelPositions[reelId];
        let x = viewPosition.x + textWidth / 12;
        return [x, sym.position.y + HTHGameConfig.symbolHeight * 1.2, 75];
    }
}